import { lazy, Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import Cookies from '@comeon/tomcat-cookies';
import { loadScript } from '../utils/globals.js';
import { requestApi } from '~/services/api.js';
import getRootElement from '~/utils/get-root-element.js';

const API_BASE_PATH = process.env.API_BASE_PATH || '';

// Uncomment if you need to resolve performance issues
if (process.env.NODE_ENV !== 'production') {
	// const { whyDidYouUpdate } = require('why-did-you-update');
	// whyDidYouUpdate(React);
	// Even better?
	// Check
	// https://github.com/welldone-software/why-did-you-render
	// for how to use it
	// const whyDidYouRender = require('@welldone-software/why-did-you-render');
	// whyDidYouRender(React);
}

function setDefaultCookie() {
	const defaultCookie = Cookies.get('locale');
	if (defaultCookie) {
		return Promise.resolve(defaultCookie);
	}
	return requestApi(`${API_BASE_PATH}/rest/state/locale/cookie`, {}, 'POST');
}

async function preloadState() {
	try {
		const { result } = await requestApi(`${API_BASE_PATH}/rest/state/state`);
		return {
			account: result.account.result,
			authentication: result.authentication.result,
			brand: result.brand.result,
			sportsbook: result.sportsbook.result,
			translations: result.translations.result,
			support: result.support.result
		};
	} catch (error) {
		throw new Error(error);
	}
}

function loadFonts({ googleFonts, typekitId, className }) {
	const WebFontConfig = {
		...(googleFonts ? { google: { families: JSON.parse(googleFonts) } } : {}),
		...(typekitId ? { typekit: { id: typekitId, class: className } } : {})
	};
	// const WebFontConfig = googleFonts
	// 	? { google: { families: JSON.parse(googleFonts) } }
	// 	: { typekit: { id: typekitId, class: className } };
	if (Object.entries(WebFontConfig).length > 0) {
		return loadScript('https://ajax.googleapis.com/ajax/libs/webfont/1.6.26/webfont.js').then(() => {
			window.WebFont.load(WebFontConfig);
		});
	}
	return Promise.resolve(WebFontConfig);
}

async function setPreloadedState() {
	if (typeof window.__PRELOADED_STATE__ === 'undefined') {
		const preloadedState = await preloadState();
		window.__PRELOADED_STATE__ = preloadedState;
	}
	return window.__PRELOADED_STATE__;
}

async function setConfiguration() {
	if (typeof window.__CONFIGURATION__ === 'undefined') {
		const configuration = await requestApi(`${API_BASE_PATH}/rest/state/configuration`);
		window.__CONFIGURATION__ = configuration.result;
	}

	return window.__CONFIGURATION__;
}

function tracker(el) {
	// Options for the observer (which mutations to observe)
	const config = { attributes: false, childList: true, subtree: true };

	// Callback function to execute when mutations are observed
	const callback = () => {
		// Use traditional 'for loops' for IE 11
		if (window.dataLayer) {
			window.dataLayer.push({ event: 'optimize.activate' });
		}
	};

	// Create an observer instance linked to the callback function
	const observer = new MutationObserver(callback);

	// Start observing the target node for configured mutations
	observer.observe(el, config);
}

async function registerServiceWorker() {
	try {
		// Use the window load event to keep the page load performant
		window.addEventListener('load', () => {
			return navigator.serviceWorker.register('/service-worker.js');
		});
	} catch (error) {
		if (process.env.NODE_ENV !== 'production') {
			throw new Error(error);
		}
	}
}

async function unregisterServiceWorker() {
	try {
		const registrations = await navigator.serviceWorker.getRegistrations();
		// eslint-disable-next-line
		for await (const registration of registrations) {
			await registration.unregister();

			const clients = window.clients.matchAll();

			clients.forEach(client => {
				if (client.url && 'navigate' in client) {
					client.navigate(client.url);
				}
			});
		}
	} catch (error) {
		if (process.env.NODE_ENV !== 'production') {
			throw new Error(error);
		}
	}
}

async function validateServiceWorker() {
	try {
		// Check that service workers are supported
		if ('serviceWorker' in navigator) {
			const { FeatureEnablePWA } = await setConfiguration();
			if (FeatureEnablePWA) {
				return registerServiceWorker();
			}
			return unregisterServiceWorker();
		}
	} catch (error) {
		// TODO handle exceptions
		if (process.env.NODE_ENV !== 'production') {
			throw new Error(error);
		}
	}
}

const Validations = () => {
	useEffect(() => {
		validateServiceWorker();
	});
	return null;
};

async function mount() {
	const rootEl = getRootElement();
	if ('MutationObserver' in window) {
		tracker(rootEl);
	}
	const { googleFonts, typekitId, className } = rootEl.dataset;
	loadFonts({ googleFonts, typekitId, className });
	const Root = lazy(() => import('./root.js'));

	const root = createRoot(rootEl);
	root.render(
		<>
			<Suspense fallback={<div className="spinner" />}>
				<Root />
			</Suspense>
			<Validations />
		</>
	);
}

async function init() {
	await setDefaultCookie();
	await Promise.all([setPreloadedState(), setConfiguration()]);
	await mount();
}

init();
